import * as React from 'react'
import { Popover as PopoverPrimitive } from 'radix-ui'

import { cn } from '~/lib/cn'
import { swallowNextClick } from '~/lib/swallow-click'

/**
 * The popover, on Radix: it floats against the viewport rather than inside whatever box it was
 * declared in, so it opens beside a card that scrolls sideways and is never cut by it. Wearing
 * the theme's overlay surface, and arriving and leaving on the editor's own float animation.
 *
 * Dismissing is the whole of what a dismissing press does — see the guidelines — and this is
 * the one place on web that has to be kept honest about it. Radix waits for the click that
 * follows an outside press before it dismisses, and only dismisses if that click went all the
 * way through, so nothing can both let it dismiss and keep the click off what was under the
 * popover. The popover therefore puts itself away on the press, as the pointer goes down, and
 * takes the click that follows out of the air; Radix's own dismissal is left with nothing to
 * do. A press on the popover's own trigger is the trigger's to answer, and a press somewhere a
 * caller says is not a dismissal — the row the popover hangs under, undo — is let through.
 */

/** Whether the popover is open, and the way to close it — what its content needs to know. */
const StateContext = React.createContext<{ open: boolean; close: () => void }>({
  open: false,
  close: () => undefined,
})

function Popover({
  open,
  defaultOpen,
  onOpenChange,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Root>) {
  const [held, setHeld] = React.useState(defaultOpen ?? false)
  const controlled = open !== undefined
  const latest = React.useRef(onOpenChange)
  React.useEffect(() => {
    latest.current = onOpenChange
  })
  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!controlled) {
        setHeld(next)
      }
      latest.current?.(next)
    },
    [controlled],
  )
  const isOpen = controlled ? open : held
  const state = React.useMemo(
    () => ({
      open: isOpen,
      close: () => {
        setOpen(false)
      },
    }),
    [isOpen, setOpen],
  )
  return (
    <StateContext.Provider value={state}>
      <PopoverPrimitive.Root data-slot="popover" open={isOpen} onOpenChange={setOpen} {...props} />
    </StateContext.Provider>
  )
}

function PopoverTrigger({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Trigger>) {
  return <PopoverPrimitive.Trigger data-slot="popover-trigger" {...props} />
}

function PopoverContent({
  className,
  align = 'center',
  sideOffset = 4,
  onPointerDownOutside,
  onPointerDownCapture,
  keepsOpenOn,
  /**
   * A popover opened from inside another one stays where it was declared: nested in the DOM,
   * it counts as inside its parent, so opening it is not what dismisses the parent. It floats
   * all the same, so the parent's box does not cut it.
   */
  portal = true,
  ...props
}: React.ComponentProps<typeof PopoverPrimitive.Content> & {
  portal?: boolean
  /**
   * Presses outside that are not dismissals: the popover stays open, and the press goes
   * through to what it landed on. For the row a popover hangs under, and for undo and redo.
   */
  keepsOpenOn?: (target: EventTarget | null) => boolean
}) {
  const { open, close } = React.useContext(StateContext)
  /*
   * Whether the press going down began inside this popover's own React tree — its content,
   * and anything it opened, a dialog portalled elsewhere included. React's capture reaches
   * here before the document's own listener below does, portals or no portals.
   */
  const inside = React.useRef(false)

  React.useEffect(() => {
    // Only while open: this wrapper is rendered whether the popover is or not.
    if (!open) {
      return
    }
    const onDown = (event: PointerEvent) => {
      const began = inside.current
      inside.current = false
      if (began) {
        return
      }
      const target = event.target
      if (target instanceof Element && target.closest('[data-slot="popover-trigger"]')) {
        return
      }
      if (keepsOpenOn?.(target)) {
        return
      }
      // The press that closes this is not also a press on what was under it.
      swallowNextClick()
      close()
    }
    // Attached a tick late, the way Radix attaches its own, so the press that opened this is
    // not the press that closes it.
    const timer = window.setTimeout(() => {
      document.addEventListener('pointerdown', onDown)
    }, 0)
    return () => {
      window.clearTimeout(timer)
      document.removeEventListener('pointerdown', onDown)
    }
  }, [open, close, keepsOpenOn])

  const content = (
    <PopoverPrimitive.Content
      data-slot="popover-content"
      align={align}
      sideOffset={sideOffset}
      onPointerDownCapture={(e) => {
        onPointerDownCapture?.(e)
        inside.current = true
      }}
      onPointerDownOutside={(e) => {
        onPointerDownOutside?.(e)
        // Where the press was let through above, Radix's own late dismissal is held off too.
        if (!e.defaultPrevented && keepsOpenOn?.(e.target)) {
          e.preventDefault()
        }
      }}
      className={cn(
        'z-50 w-72 origin-(--radix-popover-content-transform-origin) rounded-2xl bg-surface-overlay p-4 text-ink shadow-2xl ring-1 ring-white/10 outline-hidden',
        className,
      )}
      {...props}
    />
  )
  return portal ? <PopoverPrimitive.Portal>{content}</PopoverPrimitive.Portal> : content
}

function PopoverAnchor({ ...props }: React.ComponentProps<typeof PopoverPrimitive.Anchor>) {
  return <PopoverPrimitive.Anchor data-slot="popover-anchor" {...props} />
}

function PopoverHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="popover-header"
      className={cn('flex flex-col gap-1 text-sm', className)}
      {...props}
    />
  )
}

function PopoverTitle({ className, ...props }: React.ComponentProps<'h2'>) {
  return <div data-slot="popover-title" className={cn('font-medium', className)} {...props} />
}

export { Popover, PopoverTrigger, PopoverContent, PopoverAnchor, PopoverHeader, PopoverTitle }
